import { createModeratedUploadFn } from '../voiceModeration';
import { validateFile } from '../attachmentLimits';
import {
  UploadRefusalError,
  uploadRefusalText,
  validateAgainstPolicy,
  type ChatAttachmentPolicy,
  type CommunityAttachmentUpload,
} from './communityAttachments';

/**
 * CORE-404 / CORE-356 — composer upload for the backend-agnostic thread.
 *
 * The agnostic composer had no way to attach files, so circles/DMs on
 * AgapellaChatThread could only send text. This builds an uploader that
 * validates against the channel's attachment limits, runs audio through voice
 * moderation (block ⇒ reject), uploads the bytes, and returns a render-ready
 * attachment for `AttachmentView`. (CORE-618 — the GetStream uploader was
 * removed with the Stream transport; Agape.ChatApi's `POST /api/chat/attachments`
 * endpoint → S3 is the only backend now.)
 */
export interface ComposerAttachment {
  type: 'image' | 'video' | 'file' | 'voiceRecording';
  image_url?: string;
  asset_url?: string;
  thumb_url?: string;
  title?: string;
  mime_type?: string;
  file_size?: number;
  /** Voice clip length in seconds. */
  duration?: number;
  /**
   * CORE-1682 — server-side attachment id (community chat uploads). Present ⇒
   * the message goes out through `SendMessageV2` with `attachmentIds`; the
   * server builds the descriptor with a signed link itself.
   */
  id?: string;
  /** Original file name, kept verbatim (the server stores it too). */
  file_name?: string;
}

export type ComposerUpload = (
  file: File | Blob,
  meta?: { name?: string; durationSec?: number },
) => Promise<ComposerAttachment | null>;

/** Raw byte upload → the stored URL (+ optional thumbnail). Backend-specific.
 *  Matches the moderator's `UploadResult` (file required; '' ⇒ treated as failed). */
type RawUpload = (fileLike: any) => Promise<{ file: string; thumb_url?: string }>;

/** The B3 endpoint's descriptor (System.Text.Json camelCase). */
export interface ChatAttachmentDescriptor {
  type: string;
  url: string;
  thumbUrl?: string | null;
  mimeType: string;
  fileSizeBytes: number;
  durationSeconds?: number | null;
}

/**
 * Shared core: validate + voice-moderate + upload (via `rawUpload`) + shape a
 * render-ready attachment by mime type. Returns null when a clip is blocked;
 * throws on a validation error (too big / wrong type).
 */
function buildComposerUpload(
  rawUpload: RawUpload,
  attachmentLimits: any,
  onBlocked?: (message: string) => void,
): ComposerUpload {
  const moderatedUpload = createModeratedUploadFn({
    defaultUpload: rawUpload,
    getMimeType: (f: any) => f?.type || f?.mime_type || '',
    getBlob: async (f: any) => (f instanceof Blob ? f : await (await fetch(f.uri)).blob()),
    getDurationSeconds: (f: any) => (typeof f?.duration === 'number' ? f.duration : undefined),
    onBlocked: (m) => onBlocked?.(m),
  });

  return async function upload(file, meta) {
    const anyFile = file as any;
    const name = meta?.name || anyFile?.name || 'file';
    const mime: string = anyFile?.type || '';
    const size: number | undefined = anyFile?.size;

    if (attachmentLimits) {
      const error = validateFile({ name, type: mime, size: size ?? 0 }, attachmentLimits);
      if (error) throw new Error(error);
    }

    // Carry duration on the blob so the moderator can read it (voice clips).
    if (meta?.durationSec != null) anyFile.duration = meta.durationSec;

    let res: { file?: string; thumb_url?: string };
    try {
      res = await moderatedUpload(file as any);
    } catch {
      // A blocked voice clip rejects here (onBlocked already fired). Treat any
      // upload failure as "nothing attached" — the composer shows the error.
      return null;
    }
    if (!res?.file) return null;

    if (mime.startsWith('image/')) {
      return { type: 'image', image_url: res.file, thumb_url: res.thumb_url, title: name, mime_type: mime, file_size: size };
    }
    if (mime.startsWith('video/')) {
      return { type: 'video', asset_url: res.file, thumb_url: res.thumb_url, title: name, mime_type: mime, file_size: size };
    }
    if (mime.startsWith('audio/') || meta?.durationSec != null) {
      return {
        type: 'voiceRecording',
        asset_url: res.file,
        title: name || 'Голосовое сообщение',
        mime_type: mime,
        duration: meta?.durationSec,
      };
    }
    return { type: 'file', asset_url: res.file, title: name, mime_type: mime, file_size: size };
  };
}

/**
 * Build a `ComposerUpload` for the Agape.ChatApi transport (CORE-356 T6). The
 * bytes go to `POST /api/chat/attachments` (via the injected `endpointUpload`,
 * to avoid an api.ts import cycle) → S3; the returned descriptor's URL becomes
 * the attachment. Same validation + voice moderation as the Stream path.
 */
/**
 * CORE-1356, возврат QA 25.09 — потолок на загрузку вложения.
 *
 * Без него запрос к зашейпленному или заблокированному источнику **висит
 * вечно**: отказ не наступает никогда. Дальше по цепочке это выглядит так:
 * чип вложения остаётся в статусе «загружается», `canSend` из-за него
 * выключен, кнопка отправки мертва — человек видит, что вложение не
 * прикрепилось, текст остался в поле, и ничего не происходит. Ровно это и
 * прислал QA: «ответа AI не было более 2 минут».
 *
 * Этот же урок в репозитории учили дважды: CORE-119 («голый fetch к
 * зашейпленному RU-источнику висит вечно, никогда не ошибается») и CORE-806
 * («без потолка на мёртвой сети запрос просто висел, и запись выглядела
 * пропавшей»). Композер остался без него.
 *
 * Срок зависит от размера: один потолок на всё либо режет честную загрузку
 * большого видео, либо заставляет ждать минуту из-за картинки на сто
 * килобайт. Формула простая — минута на первые мегабайты и по секунде на
 * каждые сто килобайт сверху, не больше пяти минут.
 */
export const UPLOAD_TIMEOUT_FLOOR_MS = 45_000;
export const UPLOAD_TIMEOUT_CEILING_MS = 300_000;
const UPLOAD_MS_PER_100KB = 1_000;

export function uploadTimeoutFor(sizeBytes: number | undefined): number {
  const size = typeof sizeBytes === 'number' && sizeBytes > 0 ? sizeBytes : 0;
  const scaled = UPLOAD_TIMEOUT_FLOOR_MS + (size / (100 * 1024)) * UPLOAD_MS_PER_100KB;
  return Math.min(UPLOAD_TIMEOUT_CEILING_MS, Math.round(scaled));
}

export function createChatApiUpload(
  endpointUpload: (
    file: File | Blob, name: string, timeoutMs?: number,
  ) => Promise<ChatAttachmentDescriptor>,
  attachmentLimits: any,
  onBlocked?: (message: string) => void,
): ComposerUpload {
  return buildComposerUpload(
    async (f: any) => {
      const d = await endpointUpload(
        f, f?.name || 'file', uploadTimeoutFor(f?.size),
      );
      return { file: d?.url ?? '', thumb_url: d?.thumbUrl ?? undefined };
    },
    attachmentLimits,
    onBlocked,
  );
}

/**
 * CORE-1682 — composer upload for a community chat channel. Validates against
 * the channel's policy (same rules as the server, in Russian), uploads with
 * `?channelKey=` and returns a descriptor that carries the server `id`; the
 * bytes stay private until the message is sent. No voice moderation: the
 * policy has no audio formats. Refusals surface as {@link UploadRefusalError},
 * which the composer shows verbatim. The endpoint is injected to keep the
 * api.ts import cycle out, as with `createChatApiUpload`.
 */
export function createCommunityChatUpload(
  endpointUpload: (
    file: File | Blob, name: string, timeoutMs?: number,
  ) => Promise<CommunityAttachmentUpload>,
  policy: ChatAttachmentPolicy,
): ComposerUpload {
  return async function upload(file, meta) {
    const anyFile = file as any;
    const name: string = meta?.name || anyFile?.name || 'файл';
    const refusal = validateAgainstPolicy({ name, type: anyFile?.type, size: anyFile?.size ?? 0 }, policy);
    if (refusal) throw new UploadRefusalError(refusal);
    let d: CommunityAttachmentUpload;
    try {
      d = await endpointUpload(file, name, uploadTimeoutFor(anyFile?.size));
    } catch (err) {
      throw new UploadRefusalError(uploadRefusalText(err, name, policy));
    }
    if (!d?.id) throw new UploadRefusalError(uploadRefusalText(null, name, policy));
    return {
      type: d.type === 'image' ? 'image' : 'file',
      id: d.id,
      title: d.fileName || name,
      file_name: d.fileName || name,
      mime_type: d.mimeType || anyFile?.type,
      file_size: d.fileSizeBytes || anyFile?.size,
    };
  };
}
